import React, { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { includes, pathOr } from 'ramda'
import styled from 'styled-components'
import { UnorderedListOutlined } from '@ant-design/icons'
import Button from '@components/ui/Button'
import Checkbox from '@components/ui/Checkbox'
import Hr from '@components/ui/Hr'
import Modal from '@components/ui/Modal'
import { IUpsertModalMode } from '@components/ui/UpsertModal'
import useModalVisible from '@lib/hooks/useModalVisible'
import { IOrganization, useOrganizationPaginationList } from '@/organization'

type Props = {
  onChange?: (values: any[]) => any
  value?: any[]
  defaultSelectAll?: boolean
  // 若為「儲存/編輯/複製」模式需傳入 mode 判斷，一般「搜尋」模式則不需要
  mode?: IUpsertModalMode
  disabled?: boolean
}

const OrganizationListModal = React.memo<Props>(
  ({ onChange, value = [], defaultSelectAll = false, mode, disabled, ...props }: Props) => {
    const { t } = useTranslation()
    const [visible, handleVisible] = useModalVisible()

    const [checkedList, setCheckedList] = useState<any[]>(value ?? [])
    const [checkAll, setCheckAll] = useState(false)
    const [content, setContent] = React.useState<IOrganization[]>([])
    const [indeterminate, setIndeterminate] = useState(
      !!checkedList.length && checkedList.length < content.length
    )

    useOrganizationPaginationList(
      {
        pageSize: 9999,
        enabled: true,
      },
      {
        onSuccess: data => {
          setContent(pathOr([], ['data'], data))
        },
        refetchOnMount: true,
      }
    )

    const onCheckAllChange = (e: any) => {
      setCheckedList(e.target.checked ? content.map(i => i.id) : [])
      setIndeterminate(false)
      setCheckAll(e.target.checked)
    }

    const handleChange = (val: any) => {
      setCheckedList(val)
      setIndeterminate(!!val.length && val.length < content.length)
      setCheckAll(val.length === content.length)
    }

    const handleOk = React.useCallback(() => {
      handleVisible()
      onChange && onChange(checkedList)
    }, [checkedList]) //eslint-disable-line

    const handleCancel = () => {
      handleVisible()
      setCheckedList(value)
    }

    const isEditMode = React.useMemo(() => {
      return includes(mode, ['update', 'save', 'copy'])
    }, [mode])

    React.useEffect(() => {
      if (content.length === 0) {
        return
      }
      if (defaultSelectAll && !isEditMode) {
        setCheckedList(content.map(i => i.id))
        setIndeterminate(false)
        setCheckAll(true)
        onChange && onChange(content.map(i => i.id))
      }
    }, [isEditMode, content]) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
      if (isEditMode && value && content.length > 0) {
        setCheckedList(value)
        setIndeterminate(false)
        setCheckAll(value.length === content.length)
        onChange && onChange(value)
      }
    }, [isEditMode, value, content]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        <div className="flex items-center">
          <Button
            color="primary"
            icon={<UnorderedListOutlined />}
            iconPosition="right"
            onClick={handleVisible}
            disabled={disabled}>
            {t('formItems:select organization')}
          </Button>
          {checkedList.length > 0 && (
            <span className="ml-24" style={{ fontSize: 16 }}>
              <Trans
                i18nKey="formItems:count selected organization"
                values={{ count: checkedList.length }}
                components={[<span style={{ color: '#0075d3' }} className="px-4" />]}
              />
            </span>
          )}
        </div>
        <StyledModal
          width={513}
          visible={visible}
          closable={false}
          title={
            <>
              <div className="flex justify-between items-center">
                <div>
                  <label className="text-black mr-10">{t('formItems:select organization')}</label>
                </div>
                <div>
                  <Button
                    color="ghost"
                    className="mr-10"
                    style={{ minWidth: 'fit-content' }}
                    onClick={handleCancel}>
                    {t('common:Cancel')}
                  </Button>
                  <Button color="primary" style={{ minWidth: 'fit-content' }} onClick={handleOk}>
                    {t('common:Save')}
                  </Button>
                </div>
              </div>
              <Hr />
            </>
          }
          footer={null}
          {...props}>
          <Item>
            <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
              {t('formItems:organization list')}
            </Checkbox>
          </Item>

          <Checkbox.Group
            className="w-full"
            value={checkedList.map(i => i)}
            onChange={handleChange}>
            {content.map(i => (
              <Item>
                <Checkbox key={i.id} value={i.id}>
                  {i.shortName}
                </Checkbox>
              </Item>
            ))}
          </Checkbox.Group>
        </StyledModal>
      </>
    )
  }
)

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border: 1px solid ${p => p.theme.darkGrey};
  }
  .ant-modal-body {
    padding: 10px 30px 80px;
  }
`
const Item = styled.div`
  height: 39px;
  width: 100%;
  border-bottom: 1px dashed #b8b8b8;
  display: inline-flex;
  align-items: center;

  .ant-checkbox-wrapper {
    color: #000;
    font-size: 18px;
  }
`
export default OrganizationListModal
